"use client";

import { useTranslations } from "@i18n/react";
import type * as SelectPrimitive from "@radix-ui/react-select";
import type { ExoticComponent, JSX, RefAttributes } from "react";
import type { Control } from "react-hook-form";
import { useController } from "react-hook-form";

import type { StyleXArray } from "../../../../../types/styleXArray.types";
import { Select, SelectValue } from "../Select";
import { SelectContent } from "../SelectContent";
import { SelectItem } from "../SelectItem";
import { SelectTrigger } from "../SelectTrigger";

export type SelectMenuProps = SelectPrimitive.SelectProps & {
  options: { label: string; value: string }[];
  placeholder?: string;
  triggerStyleXArray?: StyleXArray;
  contentStyleXArray?: StyleXArray;
  contentProps?: SelectPrimitive.SelectContentProps;
};

export const SelectMenu = (({
  options,
  placeholder,
  triggerStyleXArray = [],
  contentStyleXArray = [],
  contentProps = {},
  ...props
}: SelectMenuProps) => {
  const t = useTranslations();
  return (
    <Select {...props}>
      <SelectTrigger styleXArray={triggerStyleXArray}>
        <SelectValue placeholder={placeholder ?? t("general_word_select")} />
      </SelectTrigger>
      <SelectContent {...contentProps} styleXArray={contentStyleXArray}>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}) as ExoticComponent<SelectMenuProps & RefAttributes<HTMLSelectElement>> & {
  Field: (props: SelectMenuFieldProps) => JSX.Element;
};

type SelectMenuFieldProps = SelectMenuProps & {
  control: Control<any, any>;
};

SelectMenu.Field = ({
  control,
  name,
  required,
  ...props
}: SelectMenuFieldProps) => {
  const {
    field: { ref, ...field },
  } = useController({
    name,
    control,
    rules: { required },
  });

  return (
    <SelectMenu
      {...field}
      {...props}
      value={field.value}
      ref={ref}
      onValueChange={(value) => field.onChange?.(value)}
    />
  );
};
